import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Promos — discount code management (`/promos` on the v4 backend).
 * Covers promo CRUD, usage history per promo. Endpoints are authenticated
 * via UMA RPT (dataCentralApi handles it).
 *
 * Backend uses limit/offset pagination (not page/page_size).
 * GET /promos returns { results, pagination }
 * GET /promos/:id returns { result }
 * GET /promos/:id/uses returns { results, pagination }
 */

/* ── Enums ─────────────────────────────────────────────────────────────── */

export type PromoType = 'percentage' | 'fixed_amount' | 'credit';
export type PromoStatus = 'active' | 'inactive' | 'expired';

/* ── Types ─────────────────────────────────────────────────────────────── */

export interface Promo {
  promo_id: number;
  code: string;
  name: string;
  description?: string;
  referrer_id?: number;

  promo_type: PromoType;
  discount_value: number;
  max_discount?: number;
  currency: string;

  max_uses?: number;
  max_uses_per_customer: number;
  current_uses: number;

  /** pgtype.Timestamp — may be null/empty for no start restriction */
  valid_from?: { Time?: string; Valid?: boolean } | null;
  /** pgtype.Timestamp — may be null/empty for no expiry */
  valid_until?: { Time?: string; Valid?: boolean } | null;

  min_order_amount?: number;
  new_customers_only: boolean;
  applicable_services?: number[];
  status: PromoStatus;

  stackable: boolean;
  category?: string;

  referrer_name?: string;
  affiliate_code?: string;

  metadata?: Record<string, unknown>;
  created_at?: { Time?: string; Valid?: boolean } | null;
  updated_at?: { Time?: string; Valid?: boolean } | null;
}

export interface PromoUse {
  use_id: number;
  promo_id: number;
  promo_code?: string;
  promo_name?: string;
  customer_id: number;
  customer_name?: string;
  customer_email?: string;
  order_id?: number;
  invoice_id?: number;
  original_amount?: number;
  discount_amount: number;
  final_amount?: number;
  /** pgtype.Timestamp */
  used_at?: { Time?: string; Valid?: boolean } | null;
}

/* ── Params / Payloads ─────────────────────────────────────────────────── */

export interface PromoListParams {
  search?: string;
  status?: string;
  referrer_id?: number;
  limit?: number;
  offset?: number;
}

export interface CreatePromoPayload {
  code: string;
  name: string;
  description?: string;
  referrer_id?: number;
  promo_type: PromoType;
  discount_value: number;
  max_discount?: number;
  currency?: string;
  max_uses?: number;
  max_uses_per_customer?: number;
  valid_from?: string;
  valid_until?: string;
  min_order_amount?: number;
  new_customers_only?: boolean;
  applicable_services?: number[];
}

export interface UpdatePromoPayload {
  name?: string;
  description?: string;
  discount_value?: number;
  max_discount?: number;
  max_uses?: number;
  max_uses_per_customer?: number;
  valid_from?: string;
  valid_until?: string;
  min_order_amount?: number;
  new_customers_only?: boolean;
  status?: PromoStatus;
}

/* ── Helpers ────────────────────────────────────────────────────────────── */

/** Resolve a pgtype.Timestamp to an ISO string or null. */
export function pgTimestampToIso(
  ts?: { Time?: string; Valid?: boolean } | null,
): string | null {
  if (!ts || !ts.Valid || !ts.Time) return null;
  return ts.Time;
}

/* ── Query keys ────────────────────────────────────────────────────────── */

export const promoKeys = {
  all: ['promos'] as const,
  list: (params: PromoListParams) => ['promos', 'list', params] as const,
  detail: (id: number) => ['promos', 'detail', id] as const,
  uses: (id: number, params?: object) => ['promos', id, 'uses', params] as const,
};

/* ── Queries ────────────────────────────────────────────────────────────── */

/** `GET /promos` — paginated promo list. */
export function usePromos(params: PromoListParams = {}) {
  return useQuery({
    queryKey: promoKeys.list(params),
    queryFn: async (): Promise<ResponseArray<Promo>> => {
      const p: Record<string, unknown> = {
        limit: params.limit ?? 20,
        offset: params.offset ?? 0,
      };
      if (params.search) p.search = params.search;
      if (params.status) p.status = params.status;
      if (params.referrer_id) p.referrer_id = params.referrer_id;
      const res = await dataCentralApi.get<ResponseArray<Promo>>(
        `/promos?${qs.stringify(p)}`,
      );
      return res.data;
    },
  });
}

/** `GET /promos/:id` — single promo detail. */
export function usePromo(id: number) {
  return useQuery({
    queryKey: promoKeys.detail(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<Promo>> => {
      const res = await dataCentralApi.get<ResponseObject<Promo>>(`/promos/${id}`);
      return res.data;
    },
  });
}

/** `GET /promos/:id/uses` — paginated usage history for a promo. */
export function usePromoUses(
  promoId: number,
  params: { limit?: number; offset?: number } = {},
) {
  return useQuery({
    queryKey: promoKeys.uses(promoId, params),
    enabled: !!promoId,
    queryFn: async (): Promise<ResponseArray<PromoUse>> => {
      const p = { limit: params.limit ?? 20, offset: params.offset ?? 0 };
      const res = await dataCentralApi.get<ResponseArray<PromoUse>>(
        `/promos/${promoId}/uses?${qs.stringify(p)}`,
      );
      return res.data;
    },
  });
}

/* ── Mutations ──────────────────────────────────────────────────────────── */

/** `POST /promos` — create a new promo code. */
export function useCreatePromo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePromoPayload): Promise<ResponseObject<Promo>> => {
      const res = await dataCentralApi.post<ResponseObject<Promo>>('/promos', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promoKeys.all });
    },
  });
}

/** `PUT /promos/:id` — update an existing promo. */
export function useUpdatePromo(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdatePromoPayload): Promise<ResponseObject<Promo>> => {
      const res = await dataCentralApi.put<ResponseObject<Promo>>(`/promos/${id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promoKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: promoKeys.all });
    },
  });
}
